import React, { useState, useMemo, useCallback } from "react";
import { FaTrashAlt, FaInfoCircle } from "react-icons/fa";
import { faXmark, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { BodyVisualizer } from "../utils/evaluationUtils";
import "../styles/HistoryModal.css";

// Registra o ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Componente do gráfico memoizado
const EvolutionChart = React.memo(({ history, exercise }) => {
  const chartConfig = useMemo(() => {
    if (!history || history.length < 2) return null;

    const labels = history.map((_, index) => `Av. ${index + 1}`);
    const isMultiResult = exercise?.resultMode === "multi";
    const datasets = [];

    if (isMultiResult) {
      datasets.push({
        label: "Lado Direito",
        data: history.map((e) => e.angles[0]),
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
        tension: 0.3,
      });
      datasets.push({
        label: "Lado Esquerdo",
        data: history.map((e) => e.angles[1]),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
        tension: 0.3,
      });
    } else {
      datasets.push({
        label: "Ângulo",
        data: history.map((e) => e.angles[0]),
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.5)",
        tension: 0.3,
      });
    }

    const data = { labels, datasets };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 12 },
          },
        },
        title: { display: false },
      },
      scales: {
        y: {
          reverse: true,
          title: { display: true, text: "Ângulo (°)", font: { size: 12 } },
        },
      },
    };
    return { data, options };
  }, [history, exercise]);

  if (!chartConfig) {
    return (
      <p style={{ color: "#151f28" }}>
        São necessárias pelo menos duas avaliações para exibir o gráfico de
        evolução.
      </p>
    );
  }

  return (
    <>
      <ChartInfo />
      <h4>Evolução</h4>
      <div className="history-chart-container">
        <Line options={chartConfig.options} data={chartConfig.data} />
      </div>
    </>
  );
});

const ChartInfo = () => {
  return (
    <div className="chart-info">
      <FaInfoCircle className="chart-info-icon" />
      <div className="chart-info-text">
        <strong>Analisando seu Progresso no Pilates</strong>
        <p className="chart-info-subtitle">
          O objetivo é <strong>diminuir o ângulo</strong>, o que representa um
          avanço na sua flexibilidade e controle.
        </p>
        <ul className="chart-info-list">
          <li>
            <span className="info-arrow">↑</span>
            <strong>Linha Subindo: Isso é progresso!</strong>
            <br />
            Significa que o ângulo diminuiu.
          </li>
          <li>
            <span className="info-arrow">↓</span>
            <strong>Linha Descendo: Ponto de atenção.</strong>
            <br />
            Indica que o ângulo aumentou.
          </li>
        </ul>
      </div>
    </div>
  );
};

const OverallProgress = ({ progressData }) => {
  if (!progressData || !progressData.hasData) {
    return (
      <p style={{ marginBottom: "20px", color: "#151f28" }}>
        São necessárias no mínimo duas avaliações para exibir o progresso geral
        do aluno.
      </p>
    );
  }

  const { average, firstDate, lastDate } = progressData;

  const getProgressDetails = (percent) => {
    if (percent >= 50) return { label: "Excelente", className: "excelente" };
    if (percent >= 30) return { label: "Muito Bom", className: "muito-bom" };
    if (percent > 0) return { label: "Bom", className: "bom" };
    return { label: "Ponto de Atenção", className: "atencao" };
  };

  const { label, className } = getProgressDetails(average);

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="overall-progress-card">
      <h4>Progresso Geral do Aluno</h4>
      <div className="progress-bar-info">
        <span className="progress-bar-label">{label}</span>
        <span className="progress-bar-percentage">
          {Math.round(average)}% de melhora
        </span>
      </div>
      <div className="progress-bar">
        <div
          className={`progress-bar-fill ${className}`}
          style={{ width: `${average}%` }}
        ></div>
      </div>

      <div className="progress-legend">
        <div className="legend-item">
          <span className="legend-color bom"></span>
          <span className="legend-text">Bom (&gt; 0%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color muito-bom"></span>
          <span className="legend-text">Muito Bom (&ge; 30%)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color excelente"></span>
          <span className="legend-text">Excelente (&ge; 50%)</span>
        </div>
      </div>

      <p className="progress-analysis-info">
        Análise baseada no período de {formatDate(firstDate)} a{" "}
        {formatDate(lastDate)}
      </p>
    </div>
  );
};

const HistoryModal = ({
  show,
  onClose,
  history,
  studentName,
  onDelete,
  exercises,
}) => {
  const [expandedExercise, setExpandedExercise] = useState(null);

  const handleToggleExercise = useCallback((exerciseName) => {
    setExpandedExercise((current) =>
      current === exerciseName ? null : exerciseName
    );
  }, []);

  const groupedHistory = useMemo(
    () =>
      history.reduce((acc, entry) => {
        const key = entry.exercise;
        if (!acc[key]) acc[key] = [];
        acc[key].push(entry);
        return acc;
      }, {}),
    [history]
  );

  const overallProgressData = useMemo(() => {
    const allProgressPercentages = [];

    for (const exerciseHistory of Object.values(groupedHistory)) {
      if (exerciseHistory.length < 2) {
        continue;
      }

      const firstEval = exerciseHistory[0];
      const lastEval = exerciseHistory[exerciseHistory.length - 1];

      const getAverageAngle = (evaluation) => {
        if (!evaluation?.angles || evaluation.angles.length === 0) return 0;
        const sum = evaluation.angles.reduce((acc, angle) => acc + angle, 0);
        return sum / evaluation.angles.length;
      };

      const avgFirstAngle = getAverageAngle(firstEval);
      const avgLastAngle = getAverageAngle(lastEval);

      if (avgFirstAngle === 0) continue;

      const improvement = avgFirstAngle - avgLastAngle;
      let percent = (improvement / avgFirstAngle) * 100;

      percent = Math.max(0, Math.min(percent, 100));
      allProgressPercentages.push(percent);
    }

    if (allProgressPercentages.length === 0) {
      return { average: 0, hasData: false };
    }

    const totalPercentage = allProgressPercentages.reduce(
      (sum, p) => sum + p,
      0
    );
    const average = totalPercentage / allProgressPercentages.length;

    const allDates = history.map((h) => new Date(h.date).getTime());
    const firstDate = new Date(Math.min(...allDates));
    const lastDate = new Date(Math.max(...allDates));

    return { average, hasData: true, firstDate, lastDate };
  }, [groupedHistory]);

  if (!show) {
    return null;
  }

  const renderComparisonItem = (entry, title, exercise) => {
    if (!entry) return null;
    return (
      <div className="history-comparison-item">
        <h5>{title}</h5>
        <p className="history-card-date">
          {new Date(entry.date).toLocaleString("pt-BR")}
        </p>
        {entry.angles?.map((angleValue, idx) => {
          const pose = entry.poses?.[`pose_${idx}`];
          if (!pose) return null;
          const angleData = entry.joints
            ? entry.joints[idx]?.points
            : exercise?.joints[idx];
          const label =
            exercise?.resultMode === "multi"
              ? idx === 0
                ? "Direito"
                : "Esquerdo"
              : "";
          return (
            <div key={idx} className="history-miniature-item">
              {label && <p>{label}</p>}
              <BodyVisualizer
                pose={pose}
                angleData={angleData}
                angleValue={angleValue}
                width={110}
                height={110}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="history-modal">
      <div className="history-content">
        <div className="history-modal-header">
          {/* TÍTULO ALTERADO PARA DUAS LINHAS */}
          <h4 className="history-modal-title">
            <span>Avaliações</span>
            <span className="student-name-subtitle">{studentName}</span>
          </h4>
          <FontAwesomeIcon
            icon={faXmark}
            className="icon history-close-icon"
            onClick={onClose}
            title="Sair"
          />
        </div>

        <div className="history-body">
          {Object.entries(groupedHistory).length > 0 && (
            <>
              <OverallProgress progressData={overallProgressData} />
            </>
          )}

          {Object.entries(groupedHistory).length === 0 ? (
            <p style={{ color: "#151f28" }}>Nenhuma avaliação registrada.</p>
          ) : (
            Object.entries(groupedHistory).map(
              ([exerciseName, exerciseHistory]) => {
                const isExpanded = expandedExercise === exerciseName;
                const exercise = exercises.find(
                  (ex) => ex.name === exerciseName
                );
                const firstEval = exerciseHistory[0];
                const lastEval = exerciseHistory[exerciseHistory.length - 1];

                return (
                  <div key={exerciseName} className="history-exercise-group">
                    <button
                      className={`history-exercise-header ${
                        isExpanded ? "expanded" : ""
                      }`}
                      onClick={() => handleToggleExercise(exerciseName)}
                    >
                      <p>{exerciseName}</p>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="history-chevron-icon"
                      />
                    </button>
                    {isExpanded && (
                      <div className="history-exercise-details">
                        <EvolutionChart
                          history={exerciseHistory}
                          exercise={exercise}
                        />

                        {exerciseHistory.length > 1 && (
                          <>
                            <h4>Comparativo</h4>
                            <div className="history-comparison-container">
                              {renderComparisonItem(
                                firstEval,
                                "Primeira Avaliação",
                                exercise
                              )}
                              {renderComparisonItem(
                                lastEval,
                                "Última Avaliação",
                                exercise
                              )}
                            </div>
                          </>
                        )}

                        <h4>Histórico Completo</h4>
                        <div className="history-scroll">
                          {exerciseHistory.map((entry, index) => (
                            <div
                              key={entry.id || index}
                              className="history-card"
                            >
                              <div className="history-card-header">
                                <p className="history-card-title">
                                  Av. {index + 1} - {entry.level}
                                </p>
                                {onDelete && (
                                  <button
                                    className="history-delete-btn"
                                    onClick={() => onDelete(entry.id)}
                                  >
                                    <FaTrashAlt />
                                  </button>
                                )}
                              </div>
                              <p className="history-card-date">
                                {new Date(entry.date).toLocaleString("pt-BR")}
                              </p>
                              <div className="history-card-poses">
                                {entry.angles?.map((angleValue, idx) => {
                                  const pose = entry.poses?.[`pose_${idx}`];
                                  if (!pose) return null;
                                  const angleData = entry.joints
                                    ? entry.joints[idx]?.points
                                    : exercise?.joints[idx];
                                  const label =
                                    exercise?.resultMode === "multi"
                                      ? idx === 0
                                        ? "Direito"
                                        : "Esquerdo"
                                      : "";
                                  return (
                                    <div
                                      key={idx}
                                      className="history-miniature-item"
                                    >
                                      {label && <p>{label}</p>}
                                      <BodyVisualizer
                                        pose={pose}
                                        angleData={angleData}
                                        angleValue={angleValue}
                                        width={100}
                                        height={100}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryModal;
